import React from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { APP_MODAL_Z_INDEX } from '@/constants/modalZIndex';

interface RenewalConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  message?: string;
}

/**
 * Modal component for showing academic renewal confirmation
 * Displayed when user is redirected from email confirmation link
 */
const RenewalConfirmationModal: React.FC<RenewalConfirmationModalProps> = ({
  open,
  onClose,
  message = 'Your academic license renewal has been confirmed successfully',
}) => {
  if (typeof document === 'undefined' || !open) {
    return null;
  }

  return createPortal(
    <Dialog open={open} onClose={onClose} style={{ position: 'relative', zIndex: APP_MODAL_Z_INDEX }}>
      <DialogBackdrop
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        style={{ zIndex: APP_MODAL_Z_INDEX }}
      />

      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: APP_MODAL_Z_INDEX }}>
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Academic License Renewal Confirmed
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                className="inline-flex w-full justify-center items-center rounded-md btn-gradient-color px-3 py-2 text-sm font-semibold text-white shadow-sm hover:from-pink-600 hover:to-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 sm:col-start-2"
                onClick={onClose}
              >
                Continue to DISGENET
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                onClick={() => window.open('mailto:info@disgenet.com', '_blank')}
              >
                Contact Support
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>,
    document.body,
  );
};

export default RenewalConfirmationModal;
